import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, SpecraBadge, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { useAcknowledgeAllNotifications, useAcknowledgeNotification, useMyNotifications } from './hooks';
import { ReviewAccessRequestDialog } from './ReviewAccessRequestDialog';
function isUnacknowledged(n) {
    return !n.acknowledgedAt;
}
// SAD-78: observed severity values are high/medium/low/info/warning, plus ""
// when an emitter omits it — render nothing for the empty case rather than an
// empty badge.
const SEVERITY_VARIANT = {
    high: 'orange',
    medium: 'warning',
    low: 'neutral',
    info: 'info',
    warning: 'warning'
};
// SAD-78/SAD-175: deep-link map keyed on source_object_type (see the
// investigation's A2 table). Every entry needs program_id, which the wire now
// carries (layer 1, SAD-78); a row with no program_id degrades to no link.
// `authz.access_request` is deliberately absent here — it keeps the existing
// in-panel ReviewAccessRequestDialog behaviour below. `authz.role_assignment`
// is also absent — there is no per-grant view today (flagged as a product
// question in the PR body), so those rows render title/body only.
//
// NOTE (pre-existing gap, not fixed here): MODIFICATION_PERIOD_CHANGED emits
// `program_modification_period` / `contract_change_set_period` source types
// (pangaea's notifyModificationPeriodChange) and neither is registered below,
// so a period-change notification silently loses its deep link too. Flagged
// for a follow-up, same shape as the option-exercise gap SAD-224 closes here.
const DEEP_LINK_ROUTES = {
    program_task: (programId, sourceObjectId)=>`/delivery/programs/${programId}/tasks?deliverable=${sourceObjectId}`,
    deliverable_instance: (programId, sourceObjectId)=>`/delivery/programs/${programId}/tasks?deliverable=${sourceObjectId}`,
    period_of_performance: (programId)=>`/delivery/programs/${programId}/periods`,
    corrective_action: (programId)=>`/delivery/programs/${programId}/audits`,
    risk: (programId)=>`/delivery/programs/${programId}/potential-issues`,
    program_modification: (programId)=>`/delivery/programs/${programId}/docs?tab=modifications`,
    contract_change_set: (programId)=>`/delivery/programs/${programId}/docs?tab=modifications`,
    // SAD-224: MODIFICATION_OPTION_EXERCISED's source id is now task-scoped
    // ("<program_modification_id>:<optional_task_id>") to survive the
    // pangaea uniqueness index, carried on a NEW source_object_type — without
    // these two entries an exercised-option notice would silently lose the
    // deep link `program_modification` already has.
    program_modification_option_exercise: (programId)=>`/delivery/programs/${programId}/docs?tab=modifications`,
    contract_change_set_option_exercise: (programId)=>`/delivery/programs/${programId}/docs?tab=modifications`,
    cpars_score: (programId)=>`/delivery/programs/${programId}/quality`,
    qasp_score: (programId)=>`/delivery/programs/${programId}/quality`,
    // SAD-834: CONTRACT_DATE_LATE's source is one contract-model date; the
    // date's drawer mounts from ?occ= on every program page.
    occurrence: (programId, sourceObjectId)=>`/delivery/programs/${programId}/deliverables?occ=${sourceObjectId}`,
    // SAD-140: WAIVER_EVIDENCE_AWAITING_REVIEW's source_object_type.
    deviation: (programId)=>`/delivery/programs/${programId}/waivers-tailoring`
};
export function deepLinkFor(n) {
    if (n.sourceObjectType === 'opportunity_activity') {
        const parts = n.sourceObjectId.split(':');
        return parts.length === 2 && parts.every(Boolean) ? `/opportunities?watchOpportunity=${encodeURIComponent(parts[0])}&watchEvent=${encodeURIComponent(parts[1])}` : null;
    }
    // "Your answer is ready": the chat the assistant finished (no program).
    if (n.sourceObjectType === 'chat_conversation' && n.sourceObjectId) {
        return `/assistant/shanks-${encodeURIComponent(n.sourceObjectId.replace(/^shanks-/, ''))}`;
    }
    // A team note waiting for approval, or the decision on one.
    if (n.sourceObjectType === 'team_memory') return '/assistant?memory=team';
    if (!n.programId) return null;
    const build = DEEP_LINK_ROUTES[n.sourceObjectType];
    return build ? build(n.programId, n.sourceObjectId) : null;
}
export function NotificationBell() {
    const { user } = useAuth();
    const companyId = user?.tenant_id ?? '';
    const { data } = useMyNotifications(companyId);
    const ack = useAcknowledgeNotification(companyId);
    const ackAll = useAcknowledgeAllNotifications(companyId);
    // WS4: an ACCESS_REQUEST_PENDING item deep-links to the review dialog
    // (its source object IS the request). D12: DELEGATION_APPROVAL_REQUESTED
    // rides the same request lane and deep-links the same way.
    const [reviewRequestId, setReviewRequestId] = useState('');
    // The bell only makes sense for an authenticated tenant user; render nothing
    // otherwise rather than throwing inside the shared layout.
    if (!companyId) return null;
    const notifications = data?.notifications ?? [];
    const hiddenCount = data?.hiddenCount ?? 0;
    const unackCount = notifications.filter(isUnacknowledged).length;
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(SpecraIconButton, {
                    variant: "ghost",
                    className: "relative",
                    "data-testid": Testids.notification.bell,
                    label: `Notifications${unackCount > 0 ? ` (${unackCount} unread)` : ''}`,
                    children: [
                        /*#__PURE__*/ _jsx(Bell, {
                            className: "h-5 w-5"
                        }),
                        unackCount > 0 && /*#__PURE__*/ _jsx("span", {
                            "data-testid": Testids.notification.bellBadge,
                            className: "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground",
                            children: unackCount > 9 ? '9+' : unackCount
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                align: "end",
                className: "w-96 p-0",
                "data-testid": Testids.notification.panel,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between border-b px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-semibold",
                                children: "Notifications"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    unackCount > 0 && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-muted-foreground",
                                        children: [
                                            unackCount,
                                            " unread"
                                        ]
                                    }),
                                    unackCount > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        "data-testid": Testids.notification.markAllReadButton,
                                        disabled: ackAll.isPending,
                                        onClick: ()=>ackAll.mutate(),
                                        children: "Mark all read"
                                    })
                                ]
                            })
                        ]
                    }),
                    hiddenCount > 0 && /*#__PURE__*/ _jsxs("p", {
                        "data-testid": Testids.notification.hiddenCount,
                        className: "border-b px-4 py-2 text-xs text-muted-foreground",
                        children: [
                            hiddenCount,
                            " ",
                            hiddenCount === 1 ? 'notification is' : 'notifications are',
                            " hidden because you don’t have access to the source ",
                            hiddenCount === 1 ? 'program' : 'programs',
                            "."
                        ]
                    }),
                    notifications.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "px-4 py-8 text-center text-sm text-muted-foreground",
                        children: "You’re all caught up — no notifications."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        "data-testid": Testids.notification.list,
                        className: "max-h-96 overflow-auto",
                        children: notifications.map((n)=>/*#__PURE__*/ _jsxs("li", {
                                "data-testid": Testids.notification.item,
                                "data-source-id": n.sourceObjectId,
                                className: `flex items-start justify-between gap-3 border-b px-4 py-3 last:border-b-0 ${isUnacknowledged(n) ? 'bg-accent/40' : ''}`,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "truncate text-sm font-medium",
                                                        children: n.title || n.notificationType
                                                    }),
                                                    n.severity && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                        variant: SEVERITY_VARIANT[n.severity] ?? 'neutral',
                                                        "data-testid": Testids.notification.severity,
                                                        children: n.severity
                                                    })
                                                ]
                                            }),
                                            n.body && /*#__PURE__*/ _jsx("p", {
                                                className: "line-clamp-2 text-xs text-muted-foreground",
                                                children: n.body
                                            }),
                                            n.dueAt && /*#__PURE__*/ _jsxs("p", {
                                                "data-testid": Testids.notification.dueDate,
                                                className: "mt-0.5 text-xs text-muted-foreground",
                                                children: [
                                                    "Due ",
                                                    n.dueAt.toDate().toLocaleDateString(undefined, {
                                                        timeZone: 'UTC'
                                                    })
                                                ]
                                            }),
                                            (n.notificationType === 'ACCESS_REQUEST_PENDING' || n.notificationType === 'DELEGATION_APPROVAL_REQUESTED') && /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                "data-testid": Testids.notification.reviewAccessButton,
                                                "data-request-id": n.sourceObjectId,
                                                className: "mt-1 text-xs font-medium text-primary hover:underline",
                                                onClick: ()=>setReviewRequestId(n.sourceObjectId),
                                                children: "Review request"
                                            }),
                                            (()=>{
                                                const href = deepLinkFor(n);
                                                if (!href) return null;
                                                return /*#__PURE__*/ _jsx(Link, {
                                                    to: href,
                                                    "data-testid": Testids.notification.deepLink,
                                                    className: "mt-1 block text-xs font-medium text-primary hover:underline",
                                                    children: "View"
                                                });
                                            })()
                                        ]
                                    }),
                                    isUnacknowledged(n) && /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        size: "sm",
                                        "data-testid": Testids.notification.ackButton,
                                        disabled: ack.isPending,
                                        onClick: ()=>ack.mutate(n.recipientId),
                                        children: "Mark read"
                                    })
                                ]
                            }, n.recipientId))
                    })
                ]
            }),
            reviewRequestId && /*#__PURE__*/ _jsx(ReviewAccessRequestDialog, {
                requestId: reviewRequestId,
                open: !!reviewRequestId,
                onOpenChange: (o)=>!o && setReviewRequestId('')
            })
        ]
    });
}
